import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBookingFiles, getStaff, assignBookingFile, parseProcessedFile, completeBookingFile, downloadFileWithName } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Funnel, DownloadSimple, UserPlus, CheckCircle, Clock, ArrowRight, Warning, XCircle, FileText, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
  processing: { label: 'Processing', color: 'bg-purple-100 text-purple-700', icon: ArrowRight },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function BookingFilesPage() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [oilFilter, setOilFilter] = useState('all');
  const [agencyFilter, setAgencyFilter] = useState('all');

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState({ open: false, fileId: null, fileName: '' });
  const [assignStaff, setAssignStaff] = useState('');
  const [assignInstruction, setAssignInstruction] = useState('');

  // Parse/Complete dialog
  const [parseDialog, setParseDialog] = useState({ open: false, fileId: null, fileName: '' });
  const [parsedEntries, setParsedEntries] = useState([]);
  const [parseLoading, setParseLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, fileId: null });
  const [detailData, setDetailData] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const oil = oilFilter === 'all' ? undefined : oilFilter;
      const [filesRes, staffRes] = await Promise.all([
        getBookingFiles(status, oil),
        isAdmin ? getStaff() : Promise.resolve({ data: [] })
      ]);
      setFiles(filesRes.data);
      setStaffList(staffRes.data.filter(s => s.role === 'staff'));
    } catch (err) {
      toast.error('Failed to load booking files');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, oilFilter, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client-side agency filter (server already does status + oil)
  const filteredFiles = agencyFilter === 'all'
    ? files
    : files.filter(f => {
        const primary = f.booking_agency || '';
        const names = f.distributor_agency_names || [];
        return primary === agencyFilter || names.includes(agencyFilter);
      });

  const exportBookingFilesExcel = async () => {
    if (filteredFiles.length === 0) { toast.error('No files to export'); return; }
    try {
      // Build simple Excel via browser — leverages SheetJS if present else CSV
      // fallback. Here we use simple CSV with status-colored HTML table + .xls
      // extension (Excel opens it happily).
      const rows = filteredFiles.map(f => ({
        Date: formatDate(f.created_at),
        Distributor: f.distributor_name || '',
        OilCompany: f.oil_company || '',
        Agency: f.booking_agency || (f.distributor_agency_names || [])[0] || '',
        File: f.original_filename || '',
        Status: (f.status || '').toUpperCase(),
        AssignedTo: f.assigned_to_name || '',
        Bookings: f.total_bookings_count || 0,
        Debited: f.total_amount_debited || 0,
        ProcessedAt: f.processed_at ? formatDate(f.processed_at) : '',
      }));
      const colorMap = {
        'COMPLETED': '#C6EFCE',
        'PROCESSING': '#FFEB9C',
        'PENDING': '#FFC7CE',
        'ASSIGNED': '#BDD7EE',
      };
      const headers = Object.keys(rows[0]);
      let html = '<html><head><meta charset="utf-8"></head><body><table border="1">';
      html += '<tr style="background:#305496;color:#fff;font-weight:bold">' +
        headers.map(h => `<th>${h}</th>`).join('') + '</tr>';
      rows.forEach(r => {
        const bg = colorMap[r.Status] || '#ffffff';
        html += `<tr style="background:${bg}">` +
          headers.map(h => `<td>${r[h]}</td>`).join('') + '</tr>';
      });
      html += '</table></body></html>';
      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BookingFiles_${agencyFilter !== 'all' ? agencyFilter.replace(/\s/g, '_') + '_' : ''}${Date.now()}.xls`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} files`);
    } catch (err) { toast.error('Export failed'); }
  };

  const handleAssign = async () => {
    if (!assignStaff) { toast.error('Staff select karo'); return; }
    try {
      await assignBookingFile(assignDialog.fileId, { staff_id: assignStaff, instruction: assignInstruction || null });
      toast.success('File assigned!');
      setAssignDialog({ open: false, fileId: null, fileName: '' });
      setAssignStaff(''); setAssignInstruction('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assign failed');
    }
  };

  const handleParse = async (fileId, fileName) => {
    setParseDialog({ open: true, fileId, fileName });
    setParseLoading(true);
    setParsedEntries([]);
    try {
      const res = await parseProcessedFile(fileId);
      setParsedEntries(res.data.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Parse failed');
      setParseDialog({ open: false, fileId: null, fileName: '' });
    } finally {
      setParseLoading(false);
    }
  };

  const handleComplete = async () => {
    const validEntries = parsedEntries.filter(e => e.matched).map(e => ({
      agency_id: e.matched_agency_id,
      quantity: e.quantity
    }));
    if (validEntries.length === 0) { toast.error('No valid entries to process'); return; }
    setCompleteLoading(true);
    try {
      const res = await completeBookingFile(parseDialog.fileId, { entries: validEntries });
      toast.success(res.data.message);
      setParseDialog({ open: false, fileId: null, fileName: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Complete failed');
    } finally {
      setCompleteLoading(false);
    }
  };

  const openDetail = async (fileId) => {
    setDetailDialog({ open: true, fileId });
    try {
      const { data } = await import('../lib/api').then(m => m.getBookingFileDetail(fileId));
      setDetailData(data);
    } catch (err) { toast.error('Failed to load details'); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${cfg.color}`}>
        <Icon size={14} weight="fill" /> {cfg.label}
      </span>
    );
  };

  if (!isAdmin) return <div className="p-4 text-red-600">Access denied.</div>;

  return (
    <div className="space-y-6" data-testid="booking-files-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Booking Files</h1>
        <div className="flex items-center gap-2">
          <Funnel size={18} className="text-gray-500" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-md h-9 px-3 text-sm" data-testid="bf-status-filter">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
          </select>
          <select value={oilFilter} onChange={e => setOilFilter(e.target.value)} className="border border-gray-200 rounded-md h-9 px-3 text-sm" data-testid="bf-oil-filter">
            <option value="all">All Companies</option>
            <option value="Bharat Gas">Bharat Gas</option>
            <option value="HPCL">HPCL</option>
            <option value="Indane">Indane</option>
          </select>
          <select value={agencyFilter} onChange={e => setAgencyFilter(e.target.value)} className="border border-gray-200 rounded-md h-9 px-3 text-sm" data-testid="bf-agency-filter">
            <option value="all">All Agencies</option>
            {Array.from(new Set(files.flatMap(f => [f.booking_agency, ...(f.distributor_agency_names || [])]).filter(Boolean))).sort().map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <button
            onClick={exportBookingFilesExcel}
            className="h-9 px-3 text-sm rounded-md border bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-semibold"
            data-testid="bf-export-btn"
          >
            📥 Export Excel
          </button>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Distributor</TableHead>
                <TableHead>Oil Company</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Processed</TableHead>
                <TableHead className="text-right">Debited</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-500">No booking files</TableCell></TableRow>
              ) : filteredFiles.map(f => (
                <TableRow key={f.id} data-testid={`bf-row-${f.id}`}>
                  <TableCell className="text-sm">{formatDate(f.created_at)}</TableCell>
                  <TableCell className="font-medium text-sm">
                    {(() => {
                      const primary = f.booking_agency
                        || (f.distributor_agency_names && f.distributor_agency_names[0])
                        || '';
                      const extra = (f.distributor_agency_names?.length || 0) > 1
                        ? ` +${f.distributor_agency_names.length - 1}`
                        : '';
                      const agencies = f.distributor_agencies || [];
                      const primaryAg = agencies.find(a => a.name === primary) || agencies[0];
                      const rate = primaryAg?.rate || 0;
                      return primary ? (
                        <>
                          <div className="text-gray-900" data-testid={`bf-agency-${f.id}`}>{primary}{extra}</div>
                          <div className="text-[11px] text-gray-500 font-normal">Owner: {f.distributor_name}</div>
                          {rate > 0 && (
                            <div className="text-[11px] text-emerald-700 font-bold mt-0.5 inline-block bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                              💰 ₹{rate}/cyl
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-gray-900">{f.distributor_name}</div>
                      );
                    })()}
                  </TableCell>
                  <TableCell><span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{f.oil_company}</span></TableCell>
                  <TableCell>
                    <button onClick={() => downloadFileWithName(`${window.location.origin}/api/booking-files/${f.id}/download`, f.url, f.original_filename)} className="text-blue-600 hover:underline text-sm flex items-center gap-1 cursor-pointer" data-testid={`download-bf-${f.id}`}>
                      <DownloadSimple size={14} /> {f.original_filename}
                    </button>
                  </TableCell>
                  <TableCell><StatusBadge status={f.status} /></TableCell>
                  <TableCell className="text-sm">
                    {f.assigned_to_name ? (
                      <>
                        <div className="font-medium text-gray-900">{f.assigned_to_name}</div>
                        {f.reassignment_count > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                            <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 font-medium">
                              🔄 Reassigned ×{f.reassignment_count}
                            </span>
                          </div>
                        )}
                        {f.auto_assigned && f.reassignment_count === 0 && (
                          <div className="text-[10px] text-emerald-700 mt-0.5">AUTO</div>
                        )}
                      </>
                    ) : f.reassignment_count > 0 ? (
                      <div>
                        <div className="text-amber-700 text-xs font-medium">Pending</div>
                        <div className="text-[10px] text-gray-500">Cancelled by {f.last_cancelled_by_name}</div>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {f.processed_file_url ? (
                      <div className="space-y-0.5">
                        <button onClick={() => downloadFileWithName(`${window.location.origin}/api/booking-files/${f.id}/download-processed`, f.processed_file_url, f.processed_file_name)} className="text-blue-600 hover:underline text-xs flex items-center gap-1 cursor-pointer" data-testid={`download-processed-${f.id}`}>
                          <FileText size={14} /> {f.processed_file_name}
                        </button>
                        {f.processed_by_name && (
                          <div className="text-[10px] text-emerald-700 font-medium">
                            ✓ By {f.processed_by_name}
                            {f.processed_at && (
                              <span className="text-gray-500 font-normal"> · {new Date(f.processed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right font-medium text-sm">{f.total_amount_debited > 0 ? fmt(f.total_amount_debited) : '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {f.status === 'pending' && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-2" onClick={() => setAssignDialog({ open: true, fileId: f.id, fileName: f.original_filename })} data-testid={`assign-bf-${f.id}`}>
                          <UserPlus size={14} className="mr-1" /> Assign
                        </Button>
                      )}
                      {f.status === 'processing' && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7 px-2" onClick={() => handleParse(f.id, f.original_filename)} data-testid={`parse-bf-${f.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Process
                        </Button>
                      )}
                      {f.status === 'completed' && (
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => openDetail(f.id)} data-testid={`detail-bf-${f.id}`}>
                          <Eye size={14} className="mr-1" /> Details
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => { if (!o) setAssignDialog({ open: false, fileId: null, fileName: '' }); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign File: {assignDialog.fileName}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Booker</Label>
              <SearchableSelect
                options={staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))}
                value={assignStaff}
                onValueChange={setAssignStaff}
                placeholder="Choose staff..."
                data-testid="assign-staff-select"
              />
            </div>
            <div>
              <Label>Instruction (Optional)</Label>
              <Input value={assignInstruction} onChange={e => setAssignInstruction(e.target.value)} placeholder="e.g. Process by today" data-testid="assign-instruction" />
            </div>
            <Button onClick={handleAssign} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-assign-btn">
              Assign to Staff
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Parse & Complete Dialog */}
      <Dialog open={parseDialog.open} onOpenChange={(o) => { if (!o) setParseDialog({ open: false, fileId: null, fileName: '' }); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Process File: {parseDialog.fileName}</DialogTitle></DialogHeader>
          {parseLoading ? (
            <div className="text-center py-8 text-gray-500">Parsing file...</div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-500">
                {parsedEntries.filter(e => e.matched).length} / {parsedEntries.length} entries matched
              </div>
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agency (from file)</TableHead>
                      <TableHead>Matched Agency</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedEntries.map((e, i) => (
                      <TableRow key={`parse-${i}-${e.agency_name || ""}`} className={e.matched ? '' : 'bg-red-50'}>
                        <TableCell className="text-sm">{e.agency_name}</TableCell>
                        <TableCell className="text-sm font-medium">
                          {e.matched ? (
                            <span className="text-green-700">{e.matched_agency_name} <span className="text-xs text-gray-400">({e.score}%)</span></span>
                          ) : (
                            <span className="text-red-600 flex items-center gap-1"><Warning size={14} /> {e.error}</span>
                          )}
                          {e.suggestions?.length > 0 && (
                            <div className="text-xs text-gray-400 mt-0.5">Did you mean: {e.suggestions.map(s => s.name).join(', ')}?</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{e.quantity}</TableCell>
                        <TableCell className="text-right text-sm">{e.matched ? fmt(e.rate) : '-'}</TableCell>
                        <TableCell className="text-right font-medium text-sm">{e.matched ? fmt(e.total_amount) : '-'}</TableCell>
                        <TableCell>
                          {e.matched ? <CheckCircle size={16} weight="fill" className="text-green-600" /> : <XCircle size={16} weight="fill" className="text-red-600" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {parsedEntries.some(e => e.matched) && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <strong>Total Debit:</strong> {fmt(parsedEntries.filter(e => e.matched).reduce((s, e) => s + (e.total_amount || 0), 0))}
                  <span className="text-gray-400 ml-2">({parsedEntries.filter(e => e.matched).length} bookings)</span>
                </div>
              )}
              <Button onClick={handleComplete} disabled={completeLoading || !parsedEntries.some(e => e.matched)} className="w-full bg-green-600 hover:bg-green-700" data-testid="confirm-complete-btn">
                {completeLoading ? 'Processing...' : `Create ${parsedEntries.filter(e => e.matched).length} Bookings & Debit Wallet`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => { if (!o) { setDetailDialog({ open: false, fileId: null }); setDetailData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>File Details</DialogTitle></DialogHeader>
          {detailData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Agency / Distributor:</span>{' '}
                  {detailData.booking_agency ? (
                    <>
                      <strong>{detailData.booking_agency}</strong>
                      <span className="text-gray-500 text-xs ml-1">(Owner: {detailData.distributor_name})</span>
                    </>
                  ) : (
                    <strong>{detailData.distributor_name}</strong>
                  )}
                </div>
                <div><span className="text-gray-500">Oil Company:</span> <strong>{detailData.oil_company}</strong></div>
                <div><span className="text-gray-500">Status:</span> <StatusBadge status={detailData.status} /></div>
                <div><span className="text-gray-500">Total Debited:</span> <strong className="text-red-600">{fmt(detailData.total_amount_debited)}</strong></div>
                <div><span className="text-gray-500">Assigned To:</span> {detailData.assigned_to_name || '-'}</div>
                <div><span className="text-gray-500">Processed By:</span> {detailData.processed_by_name || '-'}</div>
              </div>
              {detailData.bookings?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Created Bookings ({detailData.bookings.length})</h3>
                  <div className="border rounded-lg overflow-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Agency</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Fee</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailData.bookings.map(b => (
                          <TableRow key={b.id}>
                            <TableCell className="text-sm font-medium">{b.agency_name}</TableCell>
                            <TableCell className="text-right">{b.quantity}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(b.rate)}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(b.amount)}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(b.agency_conv_fee_total)}</TableCell>
                            <TableCell className="text-right font-medium text-sm">{fmt(b.amount + b.agency_conv_fee_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
